/**
 * Caret-into-view geometry for the editor's outer scroller (`.zw-editor-scroll`),
 * which owns overflow. Pure parts; `prose/scroll.ts` applies them.
 */

import { just, nothing, type Maybe } from "../lib/maybe";

export type ScrollIntoViewOptions = {
  y?: "start" | "center" | "end" | "nearest";
  /** Symmetric margin, used for both edges unless overridden below. */
  yMargin?: number;
  topMargin?: number;
  bottomMargin?: number;
  /** Defaults to instant — use `"smooth"` for caret-follow while typing. */
  behavior?: ScrollBehavior;
};

type RectLike = { top: number; bottom: number; height: number };
type CaretLike = { top: number; bottom: number };

/** Pure geometry, split out from `scrollPosIntoView` so it's testable without
 * a real `EditorView`/DOM. `y` is `start`/`center`/`end`/`nearest`. Returns `nothing` when no scroll is needed. */
export function computeScrollDelta(
  caret: CaretLike,
  scrollerRect: RectLike,
  opts: ScrollIntoViewOptions = {},
): Maybe<number> {
  const mode = opts.y ?? "nearest";
  const margin = opts.yMargin ?? 0;
  const topMargin = opts.topMargin ?? margin;
  const bottomMargin = opts.bottomMargin ?? margin;
  let delta: number;
  if (mode === "center") {
    delta =
      (caret.top + caret.bottom) / 2 -
      (scrollerRect.top + scrollerRect.height / 2);
  } else if (mode === "start") {
    delta = caret.top - (scrollerRect.top + topMargin);
  } else if (mode === "end") {
    delta = caret.bottom - (scrollerRect.bottom - bottomMargin);
  } else if (caret.top < scrollerRect.top + topMargin) {
    delta = caret.top - (scrollerRect.top + topMargin);
  } else if (caret.bottom > scrollerRect.bottom - bottomMargin) {
    delta = caret.bottom - (scrollerRect.bottom - bottomMargin);
  } else {
    return nothing;
  }
  return Math.abs(delta) < 1 ? nothing : just(delta);
}

function readCssPx(el: Element, name: string): number {
  const raw = getComputedStyle(el).getPropertyValue(name).trim();
  if (raw.length === 0) return 0;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Extra bottom clearance: soft keyboard + dock/history insets on the host. */
export function scrollBottomChromePx(host: HTMLElement): number {
  return (
    readCssPx(document.documentElement, "--zw-keyboard-inset") +
    readCssPx(host, "--zw-editor-dock-inset") +
    readCssPx(host, "--zw-editor-bottom-inset")
  );
}

/** Shrink the scroller rect so geometry ignores keyboard/dock overlay. */
export function visibleScrollerRect(
  rect: RectLike,
  bottomChromePx: number,
): RectLike {
  const chrome = Math.max(0, Math.min(bottomChromePx, rect.height));
  const height = rect.height - chrome;
  return { top: rect.top, height, bottom: rect.top + height };
}

export function applyScrollDelta(
  scroller: HTMLElement,
  delta: number,
  behavior: ScrollBehavior,
): void {
  const top = scroller.scrollTop + delta;
  if (behavior === "smooth") {
    scroller.scrollTo({ top, behavior: "smooth" });
  } else {
    scroller.scrollTop = top;
  }
}
